// Updated Chats.js with fullscreen toggle + scroll/page mode toggle
import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import Papa from 'papaparse';
import modelsCSV from '../assets/models.csv';
import { Trash } from 'lucide-react';

const PAGE_SIZE = 25;
const CHAT_MODAL_PAGE_SIZE = 2500;

const Chats = () => {
    const [chats, setChats] = useState([]);
    const [topics, setTopics] = useState([]);
    const [selectedTopic, setSelectedTopic] = useState('');
    const [chatSummary, setChatSummary] = useState(null);
    const [fullChat, setFullChat] = useState('');
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    const [showFullChatModal, setShowFullChatModal] = useState(false);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalChats, setTotalChats] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [scrollMode, setScrollMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageSize] = useState(PAGE_SIZE);
    // For chat modal
    const [modalPage, setModalPage] = useState(1);
    const [models, setModels] = useState([]);
    const [mode, setMode] = useState('list'); // 'list' or 'reader'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedReaderChatId, setSelectedReaderChatId] = useState(null);
    const [readerChatData, setReaderChatData] = useState(null); // { full, summary }
    const [showSummary, setShowSummary] = useState(false);

    // Provider color map
    const providerColors = {
        'OpenAI': '#3b82f6',      // blue
        'Anthropic': '#f59e42',  // orange
        'Google': '#facc15',     // yellow
        'Meta': '#a21caf',       // purple
        'Mistral AI': '#10b981', // teal
        'Cohere': '#f472b6',     // pink
        'xAI': '#e11d48',        // magenta/rose for Grok
        'Other': '#22c55e'       // green for undefined
    };

    useEffect(() => {
        api.get('/topics').then(res => setTopics(res.data));
        // Parse models.csv for model name lookup
        fetch(modelsCSV)
            .then(res => res.text())
            .then(text => {
                const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
                setModels(parsed.data);
            });
    }, []);

    const fetchPaginatedChats = useCallback((page) => {
        setLoading(true);
        api.get(`/chats?page=${page}&limit=${pageSize}`)
            .then(res => {
                setChats(res.data.chats);
                setTotalChats(res.data.total);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [pageSize]);

    useEffect(() => {
        if (!selectedTopic) {
            fetchPaginatedChats(currentPage);
        } else {
            // For now, fetch all chats for topic (not paginated)
            api.get(`/chats/topic/${selectedTopic}`).then(res => {
                setChats(res.data);
                setTotalChats(res.data.length);
                setCurrentPage(1);
            });
        }
    }, [currentPage, selectedTopic, fetchPaginatedChats]);

    const deleteChat = (chatId) => {
        api.delete(`/chats/${chatId}`).then(() => {
            // Refetch current page after delete
            if (!selectedTopic) {
                fetchPaginatedChats(currentPage);
            } else {
                api.get(`/chats/topic/${selectedTopic}`).then(res => {
                    setChats(res.data);
                    setTotalChats(res.data.length);
                });
            }
        });
        setDeleteConfirmId(null);
    };

    const moveChatToTopic = (chatId, newTopicId) => {
        const chat = chats.find(c => c.id === chatId);
        api.patch(`/chats/${chatId}`, {
            chat_date: chat.chat_date,
            chat_title: chat.chat_title,
            topics: [newTopicId]
        })
            .then(() => {
                if (!selectedTopic) {
                    fetchPaginatedChats(currentPage);
                } else {
                    api.get(`/chats/topic/${selectedTopic}`).then(res => {
                        setChats(res.data);
                        setTotalChats(res.data.length);
                    });
                }
            })
            .catch(err => {
                console.error('Error moving chat:', err);
                alert('Error moving chat');
            });
    };

    const viewChatSummary = (chatId) => {
        api.get(`/chats/${chatId}/summary`).then(res => {
            setChatSummary(res.data);
            setShowSummaryModal(true);
        }).catch(() => alert('Error fetching chat summary'));
    };

    const viewFullChat = (chatId) => {
        api.get(`/chats/${chatId}/full`).then(res => {
            setFullChat(res.data.chat_text);
            setModalPage(1);
            setShowFullChatModal(true);
        }).catch(() => alert('Error fetching full chat'));
    };

    // Modal pagination
    const modalTotalPages = Math.ceil(fullChat.length / CHAT_MODAL_PAGE_SIZE);
    const paginatedChat = fullChat.slice((modalPage - 1) * CHAT_MODAL_PAGE_SIZE, modalPage * CHAT_MODAL_PAGE_SIZE);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
    };

    // Pagination controls
    const totalPages = Math.ceil(totalChats / pageSize);
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }

    // Filtering logic before rendering
    const filteredChats = chats.filter(chat => {
        // Topic filter
        if (selectedTopic && !(chat.topic || []).includes(parseInt(selectedTopic))) return false;
        // Search logic
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (chat.chat_title || '').toLowerCase().includes(term);
    });

    useEffect(() => {
        if (mode === 'reader' && selectedReaderChatId) {
            setReaderChatData(null);
            setShowSummary(false);
            // Fetch full chat
            api.get(`/chats/${selectedReaderChatId}/full`).then(res => {
                const full = res;
                // Fetch summary
                api.get(`/chats/${selectedReaderChatId}/summary`).then(sumRes => {
                    setReaderChatData({ full: full.data, summary: sumRes.data });
                });
            });
        }
    }, [mode, selectedReaderChatId]);

    // Helper for model provider (handles Grok/xAI mapping)
    function getProvider(modelRow, modelCode) {
        if (modelRow) return modelRow.Provider;
        if (modelCode && ['GR0K', 'GROK', 'XAI', 'GROK-1', 'GROK-2'].includes(modelCode.toUpperCase())) return 'xAI';
        return modelCode ? 'Other' : 'Other';
    }

    return (
        <div className="terminal min-h-screen flex flex-col items-center justify-start px-4 py-6 font-mono text-green-300">
            <div className="w-full max-w-7xl">
                {/* Heading and Mode Toggle */}
                <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
                    <h2 className="text-green-400 text-2xl font-bold tracking-wide">UMA CHAT MANAGER</h2>
                    <div className="flex gap-2">
                        <button
                            className={`px-4 py-2 rounded font-bold border transition-all ${mode === 'list' ? 'bg-green-500 text-black border-green-500' : 'bg-black text-green-300 border-green-700 hover:bg-green-900'}`}
                            onClick={() => setMode('list')}
                        >
                            List Mode
                        </button>
                        <button
                            className={`px-4 py-2 rounded font-bold border transition-all ${mode === 'reader' ? 'bg-green-500 text-black border-green-500' : 'bg-black text-green-300 border-green-700 hover:bg-green-900'}`}
                            onClick={() => setMode('reader')}
                        >
                            Reader Mode
                        </button>
                    </div>
                </div>
                {/* Only List Mode is functional for now */}
                {mode === 'list' && (
                    <>
                        {/* Search/filter menu */}
                        <div className="flex flex-col sm:flex-row items-center gap-2 mb-6">
                            <input
                                type="text"
                                className="input-terminal w-full sm:w-64 text-sm"
                                placeholder="Search chats..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            <select
                                value={selectedTopic}
                                onChange={(e) => {
                                    const topicId = e.target.value;
                                    setSelectedTopic(topicId);
                                    setCurrentPage(1);
                                }}
                                className="input-terminal max-w-xs text-sm"
                            >
                                <option value="">All Topics</option>
                                {topics.map(topic => (
                                    <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                ))}
                            </select>
                        </div>
                        {loading ? (
                            <div className="text-center text-green-400 mt-10 italic opacity-70">Loading chats...</div>
                        ) : filteredChats.length === 0 ? (
                            <div className="text-center text-green-500 mt-10 italic opacity-70">
                                No chats found. Try uploading one or changing your filter.
                            </div>
                        ) : (
                            <>
                                {/* Responsive grid for chat cards */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                                    {filteredChats.map(chat => {
                                        // Map topic IDs to names for display
                                        const topicNames = chat.topic.map(tid => {
                                            const t = topics.find(tp => tp.topic_id === tid);
                                            return t ? t.topic_name : `Topic ${tid}`;
                                        });
                                        // Model name lookup
                                        const modelRow = models.find(
                                            m => m['Model Code'] && m['Model Code'].trim().toUpperCase() === (chat.model || '').trim().toUpperCase()
                                        );
                                        const provider = getProvider(modelRow, chat.model);
                                        const modelBg = providerColors[provider] || providerColors['Other'];
                                        const modelDisplay = chat.model ? `${chat.model}${modelRow ? ` (${modelRow['Model Name (Version)']})` : ''}` : 'N/A';
                                        // Primary/secondary topic IDs
                                        const primaryTopic = chat.topic[0] || '';
                                        const secondaryTopic = chat.topic[1] || '';
                                        return (
                                            <div key={chat.id} className="border border-green-700 p-4 rounded bg-black shadow-md">
                                                {/* Title and Date/Model Row */}
                                                <div className="flex items-center justify-between gap-2 mb-2">
                                                    <div className="flex flex-col flex-1 min-w-0">
                                                        <div className="text-white font-bold text-lg truncate max-w-[60vw]">{chat.chat_title}</div>
                                                        <div className="text-green-400 font-semibold text-xs sm:text-sm whitespace-nowrap">{formatDate(chat.chat_date)}</div>
                                                    </div>
                                                    <div className="flex-shrink-0">
                                                        <span
                                                            className="border border-green-700 rounded px-2 py-0.5 font-mono text-green-200 text-xs align-middle"
                                                            style={{ background: modelBg, color: '#111', borderColor: modelBg }}
                                                        >
                                                            {modelDisplay}
                                                        </span>
                                                    </div>
                                                </div>
                                                {/* Topic selectors row, right-aligned */}
                                                <div className="flex flex-col sm:flex-row gap-2 mb-1 w-full justify-end items-end">
                                                    <div className="flex flex-col w-full sm:w-auto">
                                                        <label className="text-green-400 text-xs font-bold mb-1">Primary Topic</label>
                                                        <select
                                                            onChange={e => {
                                                                const newPrimary = parseInt(e.target.value);
                                                                const newTopics = [newPrimary];
                                                                if (secondaryTopic && secondaryTopic !== newPrimary) newTopics.push(secondaryTopic);
                                                                api.patch(`/chats/${chat.id}`, {
                                                                    chat_date: chat.chat_date,
                                                                    chat_title: chat.chat_title,
                                                                    topics: newTopics
                                                                }).then(() => fetchPaginatedChats(currentPage));
                                                            }}
                                                            value={primaryTopic}
                                                            className="input-terminal w-full sm:w-36 text-sm"
                                                            style={{ minWidth: '9rem', maxWidth: '12rem' }}
                                                        >
                                                            {topics.map(topic => (
                                                                <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    <div className="flex flex-col w-full sm:w-auto">
                                                        <label className="text-green-400 text-xs font-bold mb-1">Secondary (optional)</label>
                                                        <select
                                                            onChange={e => {
                                                                const newSecondary = parseInt(e.target.value);
                                                                let newTopics = [primaryTopic];
                                                                if (newSecondary && newSecondary !== primaryTopic) newTopics.push(newSecondary);
                                                                api.patch(`/chats/${chat.id}`, {
                                                                    chat_date: chat.chat_date,
                                                                    chat_title: chat.chat_title,
                                                                    topics: newTopics
                                                                }).then(() => fetchPaginatedChats(currentPage));
                                                            }}
                                                            value={secondaryTopic || ''}
                                                            className="input-terminal w-full sm:w-36 text-sm"
                                                            style={{ minWidth: '9rem', maxWidth: '12rem' }}
                                                        >
                                                            <option value="">None</option>
                                                            {topics
                                                                .filter(topic => topic.topic_id !== parseInt(primaryTopic))
                                                                .map(topic => (
                                                                    <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                                                ))}
                                                        </select>
                                                    </div>
                                                </div>
                                                {/* Action buttons row, right-aligned */}
                                                <div className="flex flex-row gap-2 justify-end mb-2">
                                                    <button onClick={() => viewChatSummary(chat.id)} className="btn-primary px-3 py-1 text-sm">Summary</button>
                                                    <button onClick={() => viewFullChat(chat.id)} className="btn-primary px-3 py-1 text-sm">Full</button>
                                                    <button onClick={() => setDeleteConfirmId(chat.id)} className="px-3 py-1 rounded border border-red-600 bg-red-600 text-white transition-all hover:bg-transparent hover:text-red-500 hover:border-red-500 text-sm">Delete</button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {/* Pagination Controls */}
                                {!selectedTopic && totalPages > 1 && (
                                    <div className="flex justify-center items-center gap-2 mt-8">
                                        <button
                                            className="btn-primary px-3 py-1"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(currentPage - 1)}
                                        >
                                            Prev
                                        </button>
                                        {pageNumbers.map(num => (
                                            <button
                                                key={num}
                                                className={`px-3 py-1 rounded border ${currentPage === num ? 'bg-green-500 text-black border-green-500 font-bold' : 'border-green-700 text-green-300 hover:bg-green-700 hover:text-black'}`}
                                                onClick={() => setCurrentPage(num)}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                        <button
                                            className="btn-primary px-3 py-1"
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(currentPage + 1)}
                                        >
                                            Next
                                        </button>
                                    </div>
                                )}
                            </>
                        )}

                        {deleteConfirmId && (
                            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                                <div className="bg-[#0a0a0a] border border-green-500 p-6 rounded shadow-lg text-green-300 max-w-sm w-full">
                                    <p className="mb-4 text-lg font-bold text-center">⚠️ Confirm Deletion</p>
                                    <p className="text-sm text-green-400 mb-6 text-center">Are you sure you want to permanently delete this chat?</p>
                                    <div className="flex justify-between gap-4">
                                        <button onClick={() => setDeleteConfirmId(null)} className="btn-primary w-full">Cancel</button>
                                        <button onClick={() => deleteChat(deleteConfirmId)} className="bg-red-600 text-white px-4 py-2 rounded w-full">Yes, Delete</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showFullChatModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                                <div className={`bg-[#0a0a0a] border border-green-500 p-6 rounded ${isFullscreen ? 'w-screen h-screen max-h-none' : 'max-w-4xl'} w-full shadow-lg text-green-300 ${isFullscreen ? 'h-[100vh] min-h-[100vh] max-h-[100vh]' : 'max-h-[90vh]'} overflow-y-auto relative`}>
                                    <div className="flex justify-between items-center mb-2">
                                        <h4 className="text-green-400 text-xl">Full Chat (Page {modalPage}/{modalTotalPages})</h4>
                                        <div className="flex gap-2 text-sm">
                                            <button onClick={() => setScrollMode(!scrollMode)} className="btn-primary">{scrollMode ? 'Page Mode' : 'Scroll Mode'}</button>
                                            <button onClick={() => setIsFullscreen(!isFullscreen)} className="btn-primary">{isFullscreen ? 'Minimize' : 'Fullscreen'}</button>
                                        </div>
                                    </div>
                                    <pre
                                        className={
                                            "mb-4 whitespace-pre-wrap" +
                                            (!scrollMode ? " max-h-[60vh] overflow-auto" : "")
                                        }
                                    >
                                        {scrollMode ? fullChat : paginatedChat}
                                    </pre>
                                    {!scrollMode && (
                                        <div className="flex justify-between mb-4">
                                            <button disabled={modalPage === 1} onClick={() => setModalPage(modalPage - 1)} className="btn-primary">Previous</button>
                                            <button disabled={modalPage === modalTotalPages} onClick={() => setModalPage(modalPage + 1)} className="btn-primary">Next</button>
                                        </div>
                                    )}
                                    <button onClick={() => setShowFullChatModal(false)} className="btn-primary w-full">Close</button>
                                </div>
                            </div>
                        )}

                        {showSummaryModal && chatSummary && (
                            <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
                                <div className="bg-[#0a0a0a] border border-green-500 p-6 rounded max-w-xl w-full shadow-lg text-green-300 max-h-[80vh] overflow-y-auto">
                                    <h3 className="text-green-400 text-xl mb-2">{chatSummary.chat_title} (Summary)</h3>
                                    <p className="mb-1"><strong>Date:</strong> {formatDate(chatSummary.chat_date)}</p>
                                    <p className="mb-3"><strong>Topics:</strong> {chatSummary.topic.join(', ')}</p>
                                    <h4 className="text-green-500 mb-2">AI Summary:</h4>
                                    <p className="mb-4 whitespace-pre-wrap">{chatSummary.chat_summary}</p>
                                    <button onClick={() => setShowSummaryModal(false)} className="btn-primary w-full">Close</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
                {/* Reader Mode placeholder */}
                {mode === 'reader' && (
                    <div className="flex w-full max-w-7xl min-h-[70vh] gap-6 mx-auto">
                        {/* Left: Scrollable chat list */}
                        <div className="w-1/3 min-w-[220px] max-w-xs bg-[#101a10] border border-green-700 rounded-lg p-3 flex flex-col h-[70vh]">
                            {/* Search/filter menu (reuse from List Mode) */}
                            <div className="mb-3 flex flex-col gap-2">
                                <input
                                    type="text"
                                    className="input-terminal w-full text-sm"
                                    placeholder="Search chats..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                                <select
                                    value={selectedTopic}
                                    onChange={(e) => {
                                        const topicId = e.target.value;
                                        setSelectedTopic(topicId);
                                        setCurrentPage(1);
                                    }}
                                    className="input-terminal text-sm"
                                >
                                    <option value="">All Topics</option>
                                    {topics.map(topic => (
                                        <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                    ))}
                                </select>
                            </div>
                            {/* Chat list */}
                            <div className="flex-1 overflow-y-auto space-y-2 pr-1 h-[60vh]">
                                {filteredChats.length === 0 ? (
                                    <div className="text-green-500 text-center mt-8 opacity-70">No chats found.</div>
                                ) : (
                                    filteredChats.map(chat => (
                                        <div
                                            key={chat.id}
                                            className={`p-2 rounded cursor-pointer border ${selectedReaderChatId === chat.id ? 'bg-green-900 border-green-400 text-green-100' : 'bg-black border-green-800 hover:bg-green-950'} transition-all`}
                                            onClick={() => setSelectedReaderChatId(chat.id)}
                                        >
                                            <div className="font-bold truncate text-sm">{chat.chat_title}</div>
                                            <div className="text-xs text-green-400">{formatDate(chat.chat_date)}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                        {/* Right: Chat viewer */}
                        <div className="flex-1 bg-[#111] border border-green-700 rounded-lg p-6 flex flex-col overflow-hidden h-[70vh]">
                            {!selectedReaderChatId ? (
                                <div className="text-green-400 text-xl text-center opacity-60 my-auto">Select a chat to view</div>
                            ) : !readerChatData ? (
                                <div className="text-green-400 text-center my-auto animate-pulse">Loading chat...</div>
                            ) : (
                                <>
                                    {/* Header */}
                                    <div className="flex items-center justify-between gap-4 mb-2">
                                        <div className="flex flex-col items-start gap-1 w-full">
                                            <div className="text-white font-bold text-2xl leading-tight">{readerChatData.full.chat_title}</div>
                                            <div className="flex items-center gap-2 mt-1">
                                                <div className="text-green-400 text-xs font-mono">{formatDate(readerChatData.full.chat_date)}</div>
                                            </div>
                                            {/* Editable topic selectors on bottom row */}
                                            <div className="flex flex-col sm:flex-row gap-2 mt-2 w-full">
                                                <div className="flex flex-col w-full sm:w-auto">
                                                    <label className="text-green-400 text-xs font-bold mb-1">Primary Topic</label>
                                                    <select
                                                        onChange={e => {
                                                            const newPrimary = parseInt(e.target.value);
                                                            const newTopics = [newPrimary];
                                                            const secondary = readerChatData.full.topic[1];
                                                            if (secondary && secondary !== newPrimary) newTopics.push(secondary);
                                                            api.patch(`/chats/${readerChatData.full.id}`, {
                                                                chat_date: readerChatData.full.chat_date,
                                                                chat_title: readerChatData.full.chat_title,
                                                                topics: newTopics
                                                            }).then(() => {
                                                                // Refetch chat data after update
                                                                api.get(`/chats/${readerChatData.full.id}/full`).then(res => {
                                                                    api.get(`/chats/${readerChatData.full.id}/summary`).then(sumRes => {
                                                                        setReaderChatData({ full: res.data, summary: sumRes.data });
                                                                    });
                                                                });
                                                            });
                                                        }}
                                                        value={readerChatData.full.topic[0] || ''}
                                                        className="input-terminal w-full sm:w-36 text-sm"
                                                        style={{ minWidth: '9rem', maxWidth: '12rem' }}
                                                    >
                                                        {topics.map(topic => (
                                                            <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="flex flex-col w-full sm:w-auto">
                                                    <label className="text-green-400 text-xs font-bold mb-1">Secondary (optional)</label>
                                                    <select
                                                        onChange={e => {
                                                            const newSecondary = parseInt(e.target.value);
                                                            const primary = readerChatData.full.topic[0];
                                                            let newTopics = [primary];
                                                            if (newSecondary && newSecondary !== primary) newTopics.push(newSecondary);
                                                            api.patch(`/chats/${readerChatData.full.id}`, {
                                                                chat_date: readerChatData.full.chat_date,
                                                                chat_title: readerChatData.full.chat_title,
                                                                topics: newTopics
                                                            }).then(() => {
                                                                // Refetch chat data after update
                                                                api.get(`/chats/${readerChatData.full.id}/full`).then(res => {
                                                                    api.get(`/chats/${readerChatData.full.id}/summary`).then(sumRes => {
                                                                        setReaderChatData({ full: res.data, summary: sumRes.data });
                                                                    });
                                                                });
                                                            });
                                                        }}
                                                        value={readerChatData.full.topic[1] || ''}
                                                        className="input-terminal w-full sm:w-36 text-sm"
                                                        style={{ minWidth: '9rem', maxWidth: '12rem' }}
                                                    >
                                                        <option value="">None</option>
                                                        {topics
                                                            .filter(topic => topic.topic_id !== parseInt(readerChatData.full.topic[0]))
                                                            .map(topic => (
                                                                <option key={topic.topic_id} value={topic.topic_id}>{topic.topic_name}</option>
                                                            ))}
                                                    </select>
                                                </div>
                                            </div>
                                            {/* Tags under header if available */}
                                            {readerChatData.summary && readerChatData.summary.tags && readerChatData.summary.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mt-2">
                                                    {readerChatData.summary.tags.map((tag, i) => (
                                                        <span key={i} className="bg-green-900 text-green-200 px-2 py-0.5 rounded text-xs font-mono border border-green-700">{tag}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        {/* Provider logo (right side) */}
                                        <div className="flex items-center justify-end min-w-[60px] flex-col">
                                            {(() => {
                                                const modelRow = models.find(
                                                    m => m['Model Code'] && m['Model Code'].trim().toUpperCase() === (readerChatData.full.model || '').trim().toUpperCase()
                                                );
                                                const provider = getProvider(modelRow, readerChatData.full.model);
                                                if (!provider) return null;
                                                // Placeholder: use emoji or text for now
                                                const logos = {
                                                    'OpenAI': '🤖',
                                                    'Anthropic': '🦉',
                                                    'Google': '🔎',
                                                    'Meta': '📘',
                                                    'Mistral AI': '🌬️',
                                                    'Cohere': '🟣',
                                                    'xAI': '🦾',
                                                    'Other': '💾'
                                                };
                                                return <span className="text-3xl" title={provider}>{logos[provider] || '💾'}</span>;
                                            })()}
                                            {/* Delete button under provider icon */}
                                            <button
                                                className="mt-2 w-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold py-1 px-2 rounded flex items-center justify-center transition"
                                                onClick={() => {
                                                    if (window.confirm('Delete this chat? This cannot be undone.')) {
                                                        deleteChat(readerChatData.full.id);
                                                        setSelectedReaderChatId(null);
                                                    }
                                                }}
                                            >
                                                <Trash size={16} className="mr-1" />Delete
                                            </button>
                                        </div>
                                    </div>
                                    <hr className="border-green-700 mb-4" />
                                    {/* Toggle button */}
                                    <div className="flex justify-center mb-4">
                                        <button
                                            className="btn-primary px-6 py-2 text-base"
                                            onClick={() => setShowSummary(s => !s)}
                                        >
                                            {showSummary ? 'Show Chat' : 'Show Summary'}
                                        </button>
                                    </div>
                                    {/* Content area */}
                                    <div className="flex-1 overflow-y-auto bg-transparent rounded p-4 text-green-200 text-sm whitespace-pre-wrap max-h-[60vh]">
                                        {showSummary ? (
                                            <>
                                                <div className="text-green-400 font-bold mb-2">AI Summary</div>
                                                {readerChatData.summary && readerChatData.summary.chat_summary}
                                            </>
                                        ) : (
                                            readerChatData.full && readerChatData.full.chat_text
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Chats;